import { Wifi, WifiOff, Database, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface OfflineIndicatorProps {
  showWhenOnline?: boolean;
  isCached?: boolean;
  onRetry?: () => void;
  pendingCount?: number;
}

export function OfflineIndicator({
  showWhenOnline = false,
  isCached = false,
  onRetry,
  pendingCount = 0,
}: OfflineIndicatorProps) {
  const { isOnline, wasOffline } = useNetworkStatus();

  if (isOnline && !showWhenOnline && !wasOffline) {
    return null;
  }

  if (isOnline && wasOffline && onRetry) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-lg text-emerald-700 dark:text-emerald-400 text-xs">
        <Wifi className="w-3.5 h-3.5" />
        <span className="font-medium">Back online</span>
        {pendingCount > 0 && (
          <>
            <span className="text-emerald-600 dark:text-emerald-500">
              • {pendingCount} pending
            </span>
            <button
              onClick={onRetry}
              className="ml-1 flex items-center gap-1 font-semibold hover:underline"
            >
              <RefreshCw className="w-3 h-3" />
              Retry now
            </button>
          </>
        )}
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs',
          isCached
            ? 'bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400'
            : 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-400'
        )}
      >
        {isCached ? (
          <>
            <Database className="w-3.5 h-3.5" />
            <span className="font-medium">Offline • Showing cached insights</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            <span className="font-medium">Offline • Analysis will queue</span>
          </>
        )}
      </div>
    );
  }

  return null;
}
